import { ListItem, ListItemButton, ListItemIcon, useTheme } from "@mui/material";
import React, { ReactElement } from "react";
import { NavLink, NavLinkProps } from "react-router";
import ListItemText from "@mui/material/ListItemText";
import TranslationWrapper from "../../../../../components/TranslationWrapper/TranslationWrapper";
import { ArrowRight } from "@mui/icons-material";
import { useAppSelector } from "../../../../../store";
import { blackColor, hexToRgb, warningColor, whiteColor } from "../../../../../assets/jss/material-dashboard-pro-react";
import AuthenticationWrapper from "../../../../../auth/AuthenticationWrapper";

type NavigationItemBaseProps = {
  text: { tKey: string; tDefault: string } | ReactElement;
  auth: string | null;
  icon?: ReactElement;
  to?: NavLinkProps["to"];
  onClick?: () => void;
};

type NavigationItemProps =
  | (NavigationItemBaseProps & { to: string })
  | (NavigationItemBaseProps & { onClick: () => void });

export default function NavigationItem({
  text,
  auth,
  icon,
  to,
  onClick,
  ...linkProps
}: NavigationItemProps & Omit<NavLinkProps, "to">) {
  const theme = useTheme();

  let renderedText;
  if (typeof text === "object" && "tKey" in text) {
    renderedText = <TranslationWrapper {...text} />;
  } else {
    renderedText = text;
  }
  const mini = useAppSelector((state) => state.app.sidebarMiniActive && !state.app.mobileOpen);

  const element = (
    <>
      {onClick ? (
        <ListItemButton onClick={onClick} sx={{ borderRadius: 3 }}>
          <ListItemIcon sx={{ minWidth: "32px" }}>{icon ?? null}</ListItemIcon>
          <ListItemText
            primary={renderedText}
            sx={{
              transition: "all 200ms ease-in",
              opacity: mini ? 0 : 1,
              ".MuiTypography-root": {
                fontSize: "14px",
              },
            }}
          />
        </ListItemButton>
      ) : (
        <NavLink
          style={{
            color: theme.palette.text.primary,
          }}
          to={to ?? "#"}
          unstable_viewTransition
          {...linkProps}
        >
          {({ isActive }) => (
            <ListItem
              sx={{
                background: "inherit",
                borderRadius: "3px",
                ...(isActive
                  ? {
                      "&,&:hover,&:focus": {
                        color: whiteColor,
                        backgroundColor: warningColor[0],
                        boxShadow: `0 12px 20px -10px rgba(${hexToRgb(warningColor[0])},.28), 0 4px 20px 0 rgba(${hexToRgb(blackColor)},.12), 0 7px 8px -5px rgba(${hexToRgb(warningColor[0])},.2)`,
                      },
                    }
                  : {}),
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "32px",
                }}
              >
                {icon ?? <ArrowRight />}
              </ListItemIcon>
              <ListItemText
                primary={renderedText}
                sx={{
                  transition: "all 200ms ease-in",
                  opacity: mini ? 0 : 1,
                  ".MuiTypography-root": { fontSize: "14px" },
                }}
              />
            </ListItem>
          )}
        </NavLink>
      )}
    </>
  );

  if (auth) {
    return <AuthenticationWrapper permissionKey={auth}>{element}</AuthenticationWrapper>;
  }

  return element;
}
